import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FaRegEye } from 'react-icons/fa';
import { FaArrowLeftLong } from 'react-icons/fa6';
import UseOrderHistoryPage from './UseOrderHistoryPage';
import moment from 'moment';
import { Link } from 'react-router-dom';
import OrderHistoryPageMobile from './Components/OrderHistoryPageMobile';
import Navigation from '../../../layout/Navigation/Navigation';
import { ORDER_STATUS_CUSTOMER_REJECTED } from '../../../../utils/constants';

const statusColor: Record<string, string> = {
  '0': 'bg-yellow-100 text-yellow-700 border border-yellow-300',
  '1': 'bg-indigo-100 text-indigo-700 border border-indigo-300',
  '2': 'bg-orange-100 text-orange-700 border border-orange-300',
  '3': 'bg-purple-100 text-purple-700 border border-purple-300',
  '4': 'bg-green-100 text-green-700 border border-green-300',
  '5': 'bg-amber-100 text-amber-700 border border-amber-300',
  '6': 'bg-red-100 text-red-700 border border-red-300',
  '7': 'bg-red-100 text-red-700 border border-red-300',
};

const OrderHistoryPage: React.FC = () => {
  const { currentPage, displayedOrders, setCurrentPage, totalPages, recordsPerPage, navigate } = UseOrderHistoryPage();

  return (
    <>
      <div className='flex items-center justify-center bg-white px-2 md:px-6 lg:px-6 py-3 sm:py-3 md:py-8 lg:py-6 my-0 sm:my-0 md:my-3 lg:my-5'>
        <div className='bg-white w-full max-w-7xl gap-6 rounded-xl shadow-none overflow-hidden flex flex-col lg:flex-row'>
          {/* <div className="bg-white w-full mx-[.5%] sm:mx-[.5%] md:mx-[1%] lg:mx-[10%] gap-4 rounded-xl shadow-none overflow-hidden flex flex-col lg:flex-row"> */}
          <Navigation />

          <div className='flex-1 bg-white rounded-xl shadow-md md:border h-fit'>
            {/* Header */}
            <div className='flex items-center text-lg sm:text-2xl justify-between md:p-4 border-b'>
              <button className='flex lg:hidden items-center font-semibold hover:bg-red-50 rounded-lg py-1 px-2 font-poppins'>
                <FaArrowLeftLong onClick={() => navigate(-1)} className='mr-2' /> Orders History
              </button>
              <h2 className='hidden lg:block text-xl font-semibold font-poppins'>Orders History</h2>
            </div>

            {/* Desktop Table */}
            <div className='overflow-x-auto  min-w-full'>
              <table className='hidden md:table w-full align-top table-fixed border rounded-md'>
                <thead>
                  <tr className='bg-[#F2F2F2] h-10'>
                    <th className='text-center font-normal text-[#4D4D4D] font-poppins'>ORDER ID</th>
                    <th className='text-center font-normal text-[#4D4D4D] font-poppins'>DATE</th>
                    <th className='w-[80px] font-normal text-[#4D4D4D] font-poppins'>QTY</th>
                    <th className='text-center font-normal text-[#4D4D4D] font-poppins'>TOTAL</th>
                    <th className='text-center font-normal text-[#4D4D4D] font-poppins'>STATUS</th>
                    <th className=' w-[80px] font-normal text-center text-[#4D4D4D] font-poppins'>ACTION</th>
                  </tr>
                </thead>

                <tbody>
                  {displayedOrders.length > 0 ? (
                    displayedOrders.map(order => {
                      const currentStatus = order.status_timeline?.find(
                        (s: any) => s.code === order.current_status_code
                      );
                      const colorClass =
                        statusColor[order.current_status_code] || 'bg-gray-100 text-gray-700 border border-gray-300';
                      let statusText: any = currentStatus?.status || '---';
                      if (order.current_status_code === ORDER_STATUS_CUSTOMER_REJECTED) {
                        statusText = 'Rejected';
                      }
                      const qty = order.items.reduce((sum: any, item: any) => sum + item.qty, 0);

                      return (
                        <tr key={order.name} className='border-t'>
                          <td className='text-center text-[#333333] font-poppins py-5'>{order.name}</td>
                          <td className='text-center text-[#333333] font-poppins py-5'>
                            {order.status_timeline[0]?.timestamp
                              ? moment(order.status_timeline[0].timestamp).format('YYYY-MM-DD')
                              : '---'}
                          </td>
                          <td className='w-[80px] py-5 text-center text-[#333333] font-poppins'>{qty}</td>
                          <td className='text-black text-center font-poppins py-5'>₹{order.grand_total}</td>
                          <td className='text-center py-5'>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                              {statusText}
                            </span>
                          </td>
                          <td className='w-[80px] py-5 text-center font-poppins'>
                            <button className='text-red-600 hover:text-red-800'>
                              <Link to={`/orders/details/${order.name}`}>
                                <FaRegEye className='inline w-5 h-5' />
                              </Link>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className='p-2 text-center font-poppins' colSpan={5}>
                        No orders found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Pagination (desktop/tablet) */}
              {displayedOrders.length > 0 && (
                <div className='hidden md:flex items-center justify-center  gap-2  my-3'>
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className='p-2 border rounded-full disabled:opacity-50'
                  >
                    <ChevronLeft />
                  </button>

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className='p-2 border rounded-full disabled:opacity-50 flex items-center'
                  >
                    <ChevronRight />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='block sm:block md:block lg:hidden'>
        <OrderHistoryPageMobile
          displayedOrders={displayedOrders}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          recordsPerPage={recordsPerPage}
        />
      </div>
    </>
  );
};

export default OrderHistoryPage;
